import { isImmutable, List, Map } from 'immutable';

import { shapeFoldersForApp, shapeImagesForApp } from '../utils/studio/shapeForApp';
import { createController } from '../utils/controller';
import { getFilters } from '../utils/studio/filters';
import { getSize } from '../utils/iterable/getSize';
import api from '../utils/api';

import { IMAGES_PER_PAGE, ORDER_BY } from '../constants/studio';
import { MESSAGE, NOTIFICATION } from '../constants/notifications';
import { EMPTY } from '../constants';

import actions from '../actions';

const imagesController = createController();
const foldersController = createController();

function abort(controller) {
  if (controller.isActive()) {
    controller.stop();
  }
}

function abortAll() {
  [
    imagesController,
    foldersController,
  ].forEach(abort);
}

function getShopParams(state) {
  const channel = state.getIn(['selectPhotos', 'channel']);
  const db = state.getIn(['selectPhotos', 'db']);
  const shopId = state.getIn(['selectPhotos', 'shopId']);
  return { channel, db, shopId };
}

function* bootstrap(state, { channel, db, shopId }) {
  abortAll();

  yield function effect(dispatch) {
    dispatch(actions.selectPhotos.getFolders());
    dispatch(actions.selectPhotos.getImages());
  };

  return state.set('selectPhotos', Map({ channel, db, shopId }));
}

function* cleanUp(state) {
  abortAll();
  return state.delete('selectPhotos');
}

function* getFolders(state) {
  if (!state.has('selectPhotos')) return state;

  const { db, shopId } = getShopParams(state);
  const { signal } = foldersController.start();

  yield function effect(dispatch) {
    api.studio.photo.getFolders({ db, shopId, signal })
      .then(
        (response) => dispatch(actions.selectPhotos.getFolders.fulfilled(response)),
        (error) => dispatch(actions.selectPhotos.getFolders.rejected({ error, signal })),
      );
  };

  return state;
}

function* getFoldersFulfilled(state, response) {
  if (!state.has('selectPhotos')) return state;

  abort(foldersController);

  let selectPhotos = state.get('selectPhotos');
  const allPhotos = Map({ count: parseInt(response.image_count) || 0, name: 'All photos' });
  const folders = response.folders.reduce(
    shapeFoldersForApp,
    selectPhotos
      .get('folders', Map({ selected: null }))
      .set('byId', EMPTY.MAP.set(null, allPhotos))
      .set('ids', List([null]))
  );

  selectPhotos = selectPhotos
    .set('folders', folders)
    .set('loaded', selectPhotos.has('items'));

  if (selectPhotos.get('loaded') && !selectPhotos.getIn(['request', 'folder'])) {
    selectPhotos = selectPhotos.setIn(['request', 'folder'], folders.getIn(['byId', folders.get('selected')]));
  }

  return state.set('selectPhotos', selectPhotos);
}

function* getFoldersRejected(state, { error, signal }) {
  if (signal.aborted || !state.has('selectPhotos')) return state;

  abort(foldersController);

  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.LOAD_DATA,
      })
    );
  };

  return state
    .setIn(['selectPhotos', 'folders'], Map({ byId: EMPTY.MAP, ids: EMPTY.LIST }))
    .setIn(['selectPhotos', 'loaded'], state.hasIn(['selectPhotos', 'items']));
}

function* getImages(state, payload = {}) {
  if (!state.has('selectPhotos')) return state;

  const { signal } = imagesController.start();
  const { db, shopId } = getShopParams(state);
  let selectPhotos = state.get('selectPhotos').setIn(['request', 'processing'], true);
  const { folder = selectPhotos.getIn(['request', 'folder', 'uuid'], null) } = payload;
  let {
    clear = false,
    page = selectPhotos.getIn(['request', 'page'], 0),
    query = selectPhotos.getIn(['request', 'query'], ''),
  } = payload;

  if (selectPhotos.has('folders') && folder !== selectPhotos.getIn(['folders', 'selected'])) {
    selectPhotos = selectPhotos.setIn(['folders', 'selected'], folder);
  }

  if (folder !== selectPhotos.getIn(['request', 'folder', 'uuid'], null)) {
    clear = true;
    query = '';
  } else if (query !== selectPhotos.getIn(['request', 'query'], '')) {
    clear = true;
    selectPhotos = selectPhotos.set('query', query);
  }

  if (clear) {
    selectPhotos = selectPhotos.setIn(['items', 'loading'], true);
    page = 0;
  }

  const order = { by: null, type: false };

  order.by = ORDER_BY.LISTINGS_CONNECTED;
  order.type = false;

  const request = Map({
    folder: selectPhotos.getIn(['folders', 'byId', folder]),
    order: Map(order),
    page,
    query,
  });

  yield function effect(dispatch) {
    api.studio.photo.getImages({ db, filters: getFilters({ page, query, order }), folder, shopId, signal, query })
      .then(
        (response) => dispatch(actions.selectPhotos.getImages.fulfilled({ clear, request, response })),
        (error) => dispatch(actions.selectPhotos.getImages.rejected({ error, request, signal })),
      );
  };

  return state.set('selectPhotos', selectPhotos);
}

function* getImagesFulfilled(state, { clear, request, response }) {
  if (!state.has('selectPhotos')) return state;

  let selectPhotos = state.get('selectPhotos');
  let images = selectPhotos
    .get('items', Map({ byId: EMPTY.MAP, ids: EMPTY.LIST }))
    .delete('loading')
    .delete('processing')
    .set('total', parseInt(response.count));

  if (clear) {
    images = images
      .delete('selected')
      .set('byId', EMPTY.MAP)
      .set('ids', EMPTY.LIST);
  }

  selectPhotos = selectPhotos
    .set('items', response.images.reduce(shapeImagesForApp, images))
    .set('loaded', selectPhotos.has('folders'))
    .set('request', request);

  if ((request.get('page') + 1) * IMAGES_PER_PAGE < images.get('total')) {
    selectPhotos = selectPhotos.setIn(['request', 'hasMore'], true);
  }

  if (selectPhotos.get('loaded') && !request.get('folder') && getSize(selectPhotos.getIn(['folders', 'byId'])) > 0) {
    selectPhotos = selectPhotos.setIn(['request', 'folder'],
      selectPhotos.getIn(['folders', 'byId', selectPhotos.getIn(['folders', 'selected'], null)])
    );
  }

  abort(imagesController);

  return state.set('selectPhotos', selectPhotos);
}

function* getImagesRejected(state, { error, request, signal }) {
  if (signal.aborted || !state.has('selectPhotos')) return state;

  abort(imagesController);

  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.LOAD_DATA,
      })
    );
  };

  const selectPhotos = state
    .get('selectPhotos')
    .set('items', Map({ byId: EMPTY.MAP, ids: EMPTY.LIST, total: 0 }))
    .set('request', request);

  return state.set('selectPhotos', selectPhotos.set('loaded', selectPhotos.has('folders')));
}

function* setValue(state, { path, value }) {
  if (!path || !state.has('selectPhotos')) return state;

  const fullPath = Array.isArray(path)
    ? ['selectPhotos', ...path]
    : ['selectPhotos', path];

  return value === undefined
    ? state.deleteIn(fullPath)
    : state.setIn(fullPath, value);
}

function* toggleImageState(state, { images, state: path, value }) {
  if (!state.has('selectPhotos')) return state;

  function reduceImageIds(result, uuid) {
    switch (value) {
      case true: {
        return result.add(uuid);
      }

      case false: {
        return result.remove(uuid);
      }

      default: {
        return result.has(uuid)
          ? result.remove(uuid)
          : result.add(uuid);
      }
    }
  }

  const fullPath = ['selectPhotos', 'items', path];
  const imagesState = typeof images === 'string'
    ? reduceImageIds(state.getIn(fullPath, EMPTY.SET), images)
    : Array.isArray(images) || isImmutable(images)
      ? images.reduce(reduceImageIds, state.getIn(fullPath, EMPTY.SET))
      : null;

  return getSize(imagesState) > 0
    ? state.setIn(fullPath, imagesState)
    : state.deleteIn(fullPath);
}

const selectPhotos = {
  [actions.selectPhotos.bootstrap]: bootstrap,
  [actions.selectPhotos.cleanUp]: cleanUp,
  [actions.selectPhotos.getFolders]: getFolders,
  [actions.selectPhotos.getFolders.fulfilled]: getFoldersFulfilled,
  [actions.selectPhotos.getFolders.rejected]: getFoldersRejected,
  [actions.selectPhotos.getImages]: getImages,
  [actions.selectPhotos.getImages.fulfilled]: getImagesFulfilled,
  [actions.selectPhotos.getImages.rejected]: getImagesRejected,
  [actions.selectPhotos.setValue]: setValue,
  [actions.selectPhotos.toggleImageState]: toggleImageState,
};

export default selectPhotos;
